import React, { useEffect, useState } from 'react';
import SwOffCanvas from '../Common/SwOffCanvas/SwOffCanvas';
import SwBadge from '../Common/SwBadge/SwBadge';
import SwAlert from '../Common/SwAlert/SwAlert';
import SwLoader from '../Common/SwLoader/SwLoader';
import { statusMetaFor, fmtDate, fmtNumber } from './mealPlanUsersData';

const initials = name =>
  String(name || '')
    .split(' ')
    .filter(Boolean)
    .map(w => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

/**
 * MPC-11 — one employee, every wallet offer the company holds, including the
 * ones they do not hold.
 *
 * This panel is read-only by design: assigning and unassigning happen on the
 * roster, where the selection and its confirmation live. Keeping the two apart
 * means there is exactly one path to a write, and one place where its
 * consequences are stated.
 *
 * The whole picture arrives in a single call — GET /employee/{id}/plans —
 * which answers with the person, a summary and one row per offer. It replaced a
 * fan-out of one roster search per meal type: fewer requests, and no chance of
 * the rows disagreeing because they were read at different moments.
 *
 * The plans are shown BY CATEGORY, because that is how the company's offers are
 * organised. One meal type often holds a single offer, but it can hold two or
 * three, and a flat list of five plan names leaves the SPOC to work out which
 * belong together. A category heading carries its own "1 of 2 active" count, so
 * a partially removed category reads at a glance rather than needing every card
 * underneath it compared.
 */
const MealPlanUserDrawer = ({ show, onClose, employee, userId, loadPlans }) => {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  /* `bucket` is in the dependency list on purpose: after an assign or a removal
     the row behind this panel changes, and the panel should re-read rather than
     show a stale state. */
  const bucket = employee?.bucket;

  useEffect(() => {
    let cancelled = false;

    if (!show || !userId || !loadPlans) {
      setData(null);
      setFailed(false);
      return undefined;
    }

    setData(null);
    setFailed(false);

    (async () => {
      try {
        const result = await loadPlans(userId);
        if (!cancelled) setData(result);
      } catch (error) {
        console.error('Error loading this employee’s plans:', error);
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [show, userId, bucket, loadPlans]);

  if (!employee) return null;

  /* The endpoint returns the identity too, and it is preferred over the roster
     row, which can be a page or two out of date by the time this opens. */
  const person = data?.employee || employee;
  const summary = data?.summary || null;

  return (
    <SwOffCanvas
      show={show}
      onClose={onClose}
      title="Employee meal plans"
      className="MealPlanUsersDrawer"
      backdrop
      keyboard
    >
      <div className="MealPlanUsersDrawer_Head">
        <span className="MealPlanUsersDrawer_Avatar" aria-hidden="true">
          {initials(person.name)}
        </span>
        <div className="MealPlanUsersDrawer_Identity">
          <h4>{person.name}</h4>
          <p className="MealPlanUsersDrawer_Email">{person.email}</p>
          <p className="SwTextGray SwTextSmall mb-0">
            {[person.department || employee.dept, employee.location]
              .filter(Boolean)
              .join(', ')}
          </p>
        </div>
      </div>

      {/* The counts the endpoint reports itself, so the panel never derives a
          total the server already knows. */}
      {summary ? (
        <p className="SwTextGray SwTextSmall MealPlanUsersDrawer_Summary">
          {summary.active} of {summary.totalPlans} plan
          {summary.totalPlans === 1 ? '' : 's'} active
          {summary.restricted ? `, ${summary.restricted} removed` : ''}.
        </p>
      ) : null}

      <h6 className="MealPlanUsersDrawer_SectionTitle">All plans in this company</h6>

      {failed ? (
        <SwAlert variant="danger">
          Could not read this employee&apos;s plans. Close the panel and try again.
        </SwAlert>
      ) : !data ? (
        <div className="SwLoaderContainer">
          <SwLoader />
        </div>
      ) : !data.plans.length ? (
        <SwAlert variant="info" icon={<i className="bi bi-info-circle" />}>
          This company has no meal plans set up, so there is nothing to show here.
        </SwAlert>
      ) : (
        data.groups.map(group => (
          <section className="MealPlanUsersDrawer_Group" key={group.mealType}>
            <div className="MealPlanUsersDrawer_GroupHead">
              <h6 className="MealPlanUsersDrawer_GroupTitle">{group.mealType}</h6>
              {/* Every category reads the same way, whether it holds one plan or
                  four: the count, then a card per plan underneath. Naming the
                  plan here instead would repeat the card below it on the common
                  one-plan category and then change shape on the rare one. */}
              <span className="SwTextGray SwTextSmall">
                {group.active} of {group.total} active
              </span>
            </div>

            {group.plans.map(plan => {
              /* MPC-4: shape and text, never colour alone. */
              const meta = statusMetaFor(plan.status || 'Not assigned');

              return (
                <div className="MealPlanUsersDrawer_PlanRow" key={plan.id}>
                  <div className="MealPlanUsersDrawer_PlanHead">
                    <span className="MealPlanUsersDrawer_PlanName">
                      <strong>{plan.name}</strong>
                    </span>
                    <SwBadge
                      variant={meta.variant}
                      icon={<i className={`bi ${meta.icon}`} />}
                      text={meta.label}
                    />
                  </div>

                  <dl className="MealPlanUsersDrawer_PlanFacts">
                    <div>
                      <dt>Points balance</dt>
                      <dd className="MealPlanUsers_PointsCell">
                        {plan.points == null ? '–' : fmtNumber(plan.points)}
                      </dd>
                    </div>
                    <div>
                      <dt>Effective from</dt>
                      <dd>{plan.effectiveFrom ? fmtDate(plan.effectiveFrom) : '–'}</dd>
                    </div>
                    <div>
                      <dt>Valid until</dt>
                      <dd>{plan.validTo ? fmtDate(plan.validTo) : '–'}</dd>
                    </div>
                  </dl>

                  {plan.lastChangedBy ? (
                    <p className="SwTextGray SwTextSmall mb-0">
                      Last changed by {plan.lastChangedBy}
                      {plan.lastChangedOn ? ` on ${fmtDate(plan.lastChangedOn)}` : ''}.
                    </p>
                  ) : null}
                </div>
              );
            })}
          </section>
        ))
      )}

    </SwOffCanvas>
  );
};

export default MealPlanUserDrawer;
